<template>
  <header class="page-header">
    <div class="w-100 d-flex flex-column gap-2">
      <nav class="d-flex align-items-center gap-3 fs-14" aria-label="breadcrumb">
        <span class="d-flex align-items-center gap-1">
          <i class="bx bxs-chevron-left" />
          <GuButton variant="link" class="p-0 link-dark link-offset-2" @click="$router.back()">Back</GuButton>
        </span>
      </nav>

      <div class="d-flex flex-row align-items-center">
        <h1 v-if="companyName">{{ companyName }} Members</h1>

        <div class="page-button-group ms-auto">
          <GuDialog v-if="canViewEmailGroups" v-model:open="emailGroupsOpen">
            <GuDialogTrigger as-child>
              <GuButton icon="bx-group" variant="secondary">
                <span class="d-none d-sm-inline">Manage Email Groups</span>
              </GuButton>
            </GuDialogTrigger>

            <GuDialogContent v-if="selectedEmailGroup" centered>
              <template #title>
                <h5 class="modal-title fs-18">Edit Email Group</h5>
              </template>

              <ManageEmailGroupMembers :email-group="selectedEmailGroup" />

              <template #footer>
                <GuButton variant="secondary" @click="clearSelectedEmailGroup">Back</GuButton>
              </template>
            </GuDialogContent>

            <GuDialogContent v-else centered>
              <template #title>
                <h5 class="modal-title fs-18">Manage Email Groups</h5>
              </template>

              <ManageEmailGroups @email-group-selected="emailGroupSelected" />

              <template #footer>
                <GuDialogClose as-child>
                  <GuButton variant="secondary">Close</GuButton>
                </GuDialogClose>
              </template>
            </GuDialogContent>
          </GuDialog>

          <GuLink
            variant="primary"
            :href="`/CompanyManagement/add-company-member/${companyId}`"
            icon="bx-envelope"
          >
            <span class="d-none d-sm-inline">Invite User</span>
          </GuLink>

          <GuLink
            variant="primary"
            :href="`/CompanyManagement/company/${companyId}/edit`"
            icon="bx-edit"
          >
            <span class="d-none d-sm-inline">Edit Company</span>
          </GuLink>
        </div>
      </div>
    </div>
  </header>

  <div class="flex-fill-parent mx-5">
    <BlockError v-if="error" can-retry @retry="refetch">
      <h2 class="h5">Error loading members</h2>
      {{ error?.message }}
    </BlockError>

    <GuDatagridClient
      v-else
      :config="config"
      @grid-ready="onGridReady"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch, watchEffect } from 'vue'

import { GuDatagridClient } from '@gu-datagrid-new'
import { GuButton } from '@shared/gu-components/button'
import { GuLink } from '@shared/gu-components/link'
import { GuDialog, GuDialogClose, GuDialogContent, GuDialogTrigger } from '@shared/gu-components/dialog'
import BlockError from '@shared/components/BlockError.vue'

import { useTriggerRule } from '@companies/api/companies.api'
import { useCompanyStore } from '@companies/stores/CompanyStore'
import ManageEmailGroups from '@companies/components/ManageEmailGroups.vue'
import ManageEmailGroupMembers from '@companies/components/ManageEmailGroupMembers.vue'

import { buildPgeMembersGridConfig } from '@customers/pge/views/companies/companies.members.datagrid.config'

const props = defineProps({
  companyId: {
    type: [String, Number],
    required: true,
  },
})

const companyStore = useCompanyStore()

// Path B: invoke the `GetCompanyUsers` rule which returns
// `{companyName, companyId, companyUsers: [{Full Name, Account Name, Email, Type,
//  Application Access, Status, Is Invited, Actions: [...]}, ...]}`.
// The rule joins Pending Installers, Approved Installers, and Company Admins
// from `otherFields`, matching the legacy Vue2 PG&E member view.
const userName = window.siteVars?.user?.name || ''
const privileges = window.siteVars?.user?.privileges || []

const canViewEmailGroups =
  privileges.includes('viewCompanyEmailGroups') ||
  privileges.includes('editCompanyEmailGroups') ||
  privileges.includes('editAllCompanies')

const { data, isPending: isLoading, error, refetch } = useTriggerRule(
  'GetCompanyUsers',
  { userName, companyId: props.companyId },
)

const companyName = computed(() => data.value?.companyName ?? '')

// Hydrate the company store from the rule response so that ManageEmailGroups
// (which reads from useCompanyStore) has the company id + name available.
watch(data, (val) => {
  if (val?.companyId != null) {
    companyStore.setCompany({
      id: val.companyId,
      name: val.companyName,
    })
  }
}, { immediate: true })

onBeforeUnmount(() => companyStore.resetCompany())

const emailGroupsOpen = ref(false)
const selectedEmailGroup = ref(null)
function emailGroupSelected (group) {
  selectedEmailGroup.value = group
}
function clearSelectedEmailGroup () {
  selectedEmailGroup.value = null
}

const gridApi = ref(null)
const config = ref(buildPgeMembersGridConfig({
  rowData: [],
  context: { refetch, companyName: '', companyId: props.companyId },
}))

function onGridReady (event) {
  gridApi.value = event.api
  if (isLoading.value) {
    gridApi.value.setGridOption('loading', true)
  }
}

watchEffect(() => {
  if (!gridApi.value) return
  gridApi.value.setGridOption('loading', isLoading.value)
  // Refresh the context (companyName, admin count) so member-action renderers
  // can read them. Admin count drives the one-admin-required guard in Remove.
  const rows = Array.isArray(data.value?.companyUsers) ? data.value.companyUsers.map(normalizeMember) : []
  const adminCount = rows.filter((r) => isAdmin(r) && (r.Status === 'Active' || r.Status === 'Approved')).length
  gridApi.value.setGridOption('context', {
    refetch,
    companyName: companyName.value,
    companyId: props.companyId,
    adminCount,
  })
  if (!isLoading.value) {
    gridApi.value.setGridOption('rowData', rows)
  }
})

function isAdmin (member) {
  const t = String(member.Type ?? '').toLowerCase()
  return t === 'admin'
}

function normalizeMember (member) {
  // PG&E rule statuses are mixed-case: 'Active' for admins, 'Approved' for
  // members, 'Pending' for installer requests, 'Disabled' for disabled. The
  // 'Pending' branch splits into 'Invited' (CompanyAdmin invited the user) vs
  // 'Request Pending' (user requested to join). Match Vue2 behavior.
  let status = String(member.Status ?? '')
  // Lowercase variants come back from the Java endpoint when the rule isn't
  // available - normalize them so badge variants resolve.
  status = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  if (status === 'Active' || status === 'ACTIVE') status = 'Active'
  if (status === 'Pending') {
    status = member['Is Invited'] ? 'Invited' : 'Request Pending'
  }
  return {
    ...member,
    Status: status,
  }
}
</script>
